import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { installmentService } from '../services/installmentService';
import { generateMonthRange, formatMonthLabel } from '../../../utils/monthRange';

interface Installment { _id: string; description: string; months: Record<string, number>; groupName?: string; classifierName?: string; }

const ALL_MONTHS = generateMonthRange();
const formatMonth = formatMonthLabel;
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Groups installments by group name and classifier name
function groupItems(items: Installment[]) {
  const grouped: Record<string, Record<string, Installment[]>> = {};
  for (const item of items) {
    const grp = item.groupName || 'Sem agrupador';
    const cls = item.classifierName || 'Sem classificação';
    grouped[grp] ??= {};
    grouped[grp][cls] ??= [];
    grouped[grp][cls].push(item);
  }
  return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
}

// Returns whether an installment has any non-zero value in the given months
function hasValueInMonths(item: Installment, months: string[]): boolean {
  return months.some(m => (item.months?.[m] || 0) > 0);
}

// Filters and builds the visible classifier+items list for mobile card groups
function getVisibleClasses(
  classes: Record<string, Installment[]>,
  visibleMonths: string[]
): { cls: string; clsItems: Installment[] }[] {
  return Object.entries(classes)
    .map(([cls, clsItems]) => ({
      cls,
      clsItems: clsItems.filter(item => hasValueInMonths(item, visibleMonths)),
    }))
    .filter(({ clsItems }) => clsItems.length > 0);
}

const InstallmentPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<Installment[]>([]);
  const [newDesc, setNewDesc] = useState('');
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    try { const s = localStorage.getItem('fin_installment_months'); if (s) return JSON.parse(s); } catch {}
    const now = new Date(); return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
  });

  const visibleMonths = useMemo(() => ALL_MONTHS.filter(m => selectedMonths.includes(m)), [selectedMonths]);
  const showTotals = visibleMonths.length > 1;

  const toggleMonth = (m: string) => {
    setSelectedMonths(prev => {
      const next = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m].sort((a, b) => a.localeCompare(b));
      localStorage.setItem('fin_installment_months', JSON.stringify(next));
      return next;
    });
  };
  const selectAllMonths = () => { setSelectedMonths(ALL_MONTHS); localStorage.setItem('fin_installment_months', JSON.stringify(ALL_MONTHS)); };
  const clearMonths = () => { setSelectedMonths([]); localStorage.setItem('fin_installment_months', JSON.stringify([])); };

  const load = useCallback(async () => {
    const res = await installmentService.list();
    setItems(res.data.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newDesc.trim()) { return; }
    setSaving(true);
    await installmentService.upsert({ description: newDesc.trim(), months: {} });
    setNewDesc('');
    await load();
    setSaving(false);
  };

  const handleAmountChange = async (item: Installment, month: string, value: number) => {
    const newMonths = { ...(item.months || {}), [month]: value };
    if (value === 0) { delete newMonths[month]; }
    setSaving(true);
    await installmentService.upsert({ id: item._id, description: item.description, months: newMonths });
    await load();
    setSaving(false);
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Apagar parcelamento e todas as transações associadas?')) { return; }
    setSaving(true);
    await installmentService.remove(id);
    await load();
    setSaving(false);
  };

  const handleDescChange = async (item: Installment, newDescription: string) => {
    if (!newDescription.trim() || newDescription === item.description) { return; }
    setSaving(true);
    await installmentService.upsert({ id: item._id, description: newDescription.trim(), months: item.months || {} });
    await load();
    setSaving(false);
  };

  const grouped = useMemo(() => groupItems(items), [items]);
  const totalGeral = useMemo(() => items.reduce((s, item) => s + visibleMonths.reduce((a, m) => a + (item.months?.[m] || 0), 0), 0), [items, visibleMonths]);

  if (loading) { return <LoadingSpinner />; }

  return (
    <>
      <PageHeader title="Parcelamentos" />

      {/* Month selector */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-gray-600">Competências:</span>
          <button onClick={selectAllMonths} className="text-xs text-blue-600 hover:underline">Todas</button>
          <button onClick={clearMonths} className="text-xs text-blue-600 hover:underline">Nenhuma</button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 md:flex md:flex-wrap gap-1">
          {ALL_MONTHS.map(m => (
            <button key={m} onClick={() => toggleMonth(m)}
              className={`text-xs px-2 py-1.5 sm:py-0.5 rounded border text-center ${selectedMonths.includes(m) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
            >{formatMonth(m)}</button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Add new */}
        <div className="flex gap-2">
          <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Descrição do novo parcelamento"
            onKeyDown={e => { if (e.key === 'Enter' && newDesc.trim()) { handleAdd(); } }}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button onClick={handleAdd} disabled={!newDesc.trim()} className="shrink-0 flex items-center justify-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Adicionar</span>
          </button>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-3">
          {grouped.map(([grp, classes]) => {
            const visibleClasses = getVisibleClasses(classes, visibleMonths);
            if (visibleClasses.length === 0) return null;
            return (
            <div key={grp} className="border rounded-lg overflow-hidden">
              <div className="bg-blue-50 px-3 py-2 border-b">
                <span className="font-semibold text-blue-800 text-sm">{grp}</span>
              </div>
              {visibleClasses.map(({ cls, clsItems }) => (
                <div key={cls} className="border-b last:border-b-0">
                  <div className="bg-gray-50 px-3 py-1.5">
                    <span className="text-xs font-medium text-gray-600">{cls}</span>
                  </div>
                  {clsItems.map(item => (
                    <InstallmentCard key={item._id} item={item} visibleMonths={visibleMonths} onAmountChange={handleAmountChange} onRemove={handleRemove} onDescChange={handleDescChange} />
                  ))}
                </div>
              ))}
            </div>
            );
          })}
          {/* Grand Total Mobile */}
          <div className="bg-gray-100 border rounded-lg px-3 py-2 flex justify-between items-center">
            <span className="font-semibold text-gray-900 text-sm">Total Geral</span>
            <span className="font-bold text-red-700 text-sm">{fmtBRL(totalGeral)}</span>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:flex sm:flex-col sm:items-center">
          <div className="w-fit max-w-full">
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 min-w-[250px]">Agrupador / Classificação / Descrição</th>
                      {visibleMonths.map((m, i) => (
                        <React.Fragment key={m}>
                          {i > 0 && <th className="w-6 bg-gray-50" />}
                          <th className="px-2 py-2 text-center font-medium text-gray-600 min-w-[80px]">{formatMonth(m)}</th>
                        </React.Fragment>
                      ))}
                      {showTotals && <th className="px-2 py-2 text-center font-medium text-gray-600">Total</th>}
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(([grp, classes]) => (
                      <GroupSection key={grp} grp={grp} classes={classes} visibleMonths={visibleMonths} showTotals={showTotals} onAmountChange={handleAmountChange} onRemove={handleRemove} onDescChange={handleDescChange} />
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t">
                    <tr>
                      <td className="px-3 py-2 font-semibold text-gray-900 sticky left-0 bg-gray-100">Total Geral</td>
                      {visibleMonths.map((m, i) => {
                        const total = items.reduce((s, item) => s + (item.months?.[m] || 0), 0);
                        return <React.Fragment key={m}>{i > 0 && <td className="bg-gray-100" />}<td className={`px-2 py-2 text-center font-semibold ${total ? 'text-red-700' : 'text-gray-300'}`}>{total ? fmtBRL(total) : '-'}</td></React.Fragment>;
                      })}
                      {showTotals && <td className="px-2 py-2 text-center font-bold text-red-700">{fmtBRL(totalGeral)}</td>}
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingOverlay isVisible={saving} message="Salvando..." />
    </>
  );
};

// Group section row in the desktop table
const GroupSection: React.FC<{
  grp: string;
  classes: Record<string, Installment[]>;
  visibleMonths: string[];
  showTotals: boolean;
  onAmountChange: (item: Installment, month: string, value: number) => void;
  onRemove: (id: string) => void;
  onDescChange: (item: Installment, desc: string) => void;
}> = ({ grp, classes, visibleMonths, showTotals, onAmountChange, onRemove, onDescChange }) => {
  const grpTotal = Object.values(classes).flat().reduce((s, i) => s + visibleMonths.reduce((a, m) => a + (i.months?.[m] || 0), 0), 0);
  return (
    <React.Fragment>
      <tr className="bg-blue-50 border-b">
        <td className="px-3 py-2 font-semibold text-blue-800 sticky left-0 bg-blue-50">{grp}</td>
        {visibleMonths.map((m, i) => {
          const mTotal = Object.values(classes).flat().reduce((s, i) => s + (i.months?.[m] || 0), 0);
          return <React.Fragment key={m}>{i > 0 && <td className="bg-blue-50" />}<td className={`px-2 py-2 text-center font-semibold ${mTotal ? 'text-red-700' : 'text-gray-300'}`}>{mTotal ? fmtBRL(mTotal) : '-'}</td></React.Fragment>;
        })}
        {showTotals && <td className="px-2 py-2 text-center font-bold text-red-700">{grpTotal ? fmtBRL(grpTotal) : '-'}</td>}
        <td></td>
      </tr>
      {Object.entries(classes).sort(([a], [b]) => a.localeCompare(b)).map(([cls, clsItems]) => (
        <ClassifierSection key={cls} cls={cls} clsItems={clsItems} visibleMonths={visibleMonths} showTotals={showTotals} onAmountChange={onAmountChange} onRemove={onRemove} onDescChange={onDescChange} />
      ))}
    </React.Fragment>
  );
};

// Classifier section row in the desktop table
const ClassifierSection: React.FC<{
  cls: string;
  clsItems: Installment[];
  visibleMonths: string[];
  showTotals: boolean;
  onAmountChange: (item: Installment, month: string, value: number) => void;
  onRemove: (id: string) => void;
  onDescChange: (item: Installment, desc: string) => void;
}> = ({ cls, clsItems, visibleMonths, showTotals, onAmountChange, onRemove, onDescChange }) => {
  const clsTotal = clsItems.reduce((s, i) => s + visibleMonths.reduce((a, m) => a + (i.months?.[m] || 0), 0), 0);
  return (
    <React.Fragment>
      <tr className="bg-gray-50 border-b">
        <td className="px-1 py-1.5 pl-3 sm:px-3 sm:pl-6 font-medium text-gray-700 sticky left-0 bg-gray-50">{cls}</td>
        {visibleMonths.map((m, i) => {
          const mTotal = clsItems.reduce((s, i) => s + (i.months?.[m] || 0), 0);
          return <React.Fragment key={m}>{i > 0 && <td className="bg-gray-50" />}<td className={`px-2 py-1.5 text-center font-medium ${mTotal ? 'text-red-600' : 'text-gray-300'}`}>{mTotal ? fmtBRL(mTotal) : '-'}</td></React.Fragment>;
        })}
        {showTotals && <td className="px-2 py-1.5 text-center font-semibold text-red-700">{clsTotal ? fmtBRL(clsTotal) : '-'}</td>}
        <td></td>
      </tr>
      {clsItems.map(item => (
        <InstallmentRow key={item._id} item={item} visibleMonths={visibleMonths} showTotals={showTotals} onAmountChange={onAmountChange} onRemove={onRemove} onDescChange={onDescChange} />
      ))}
    </React.Fragment>
  );
};

const InstallmentRow: React.FC<{
  item: Installment;
  visibleMonths: string[];
  showTotals: boolean;
  onAmountChange: (item: Installment, month: string, value: number) => void;
  onRemove: (id: string) => void;
  onDescChange: (item: Installment, desc: string) => void;
}> = ({ item, visibleMonths, showTotals, onAmountChange, onRemove, onDescChange }) => {
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(item.description);
  const months = item.months || {};

  const total = visibleMonths.reduce((s, m) => s + (months[m] || 0), 0);

  const startEdit = (month: string) => {
    setEditingMonth(month);
    setEditValue(months[month] ? String(months[month]) : '');
  };

  const saveEdit = () => {
    if (editingMonth) {
      const num = parseFloat(editValue.replace(',', '.')) || 0;
      if (num !== (months[editingMonth] || 0)) { onAmountChange(item, editingMonth, num); }
      setEditingMonth(null);
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-3 py-2 pl-10 sticky left-0 bg-white">
        {editingDesc ? (
          <input autoFocus value={descValue} onChange={e => setDescValue(e.target.value)}
            onBlur={() => { onDescChange(item, descValue); setEditingDesc(false); }}
            onKeyDown={e => { if (e.key === 'Enter') { onDescChange(item, descValue); setEditingDesc(false); } if (e.key === 'Escape') { setEditingDesc(false); } }}
            className="border border-blue-300 rounded px-2 py-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500" />
        ) : (
          <button type="button" className="text-left text-gray-600 hover:text-blue-600" onDoubleClick={() => setEditingDesc(true)}>{item.description}</button>
        )}
      </td>
      {visibleMonths.map((m, i) => (
        <React.Fragment key={m}>
          {i > 0 && (
            <td className="w-6 text-center">
              <button onClick={() => { const prev = months[visibleMonths[i - 1]] || 0; if (prev !== (months[m] || 0)) onAmountChange(item, m, prev); }} className="text-gray-400 hover:text-blue-600 text-xs">→</button>
            </td>
          )}
          <td className="px-2 py-2 text-center">
            {editingMonth === m ? (
              <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                onBlur={saveEdit} onKeyDown={e => { if (e.key === 'Enter') { saveEdit(); } if (e.key === 'Escape') { setEditingMonth(null); } }}
                className="w-16 border border-blue-300 rounded px-1 py-0.5 text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-500" />
            ) : (
              <button type="button" className={`text-sm ${months[m] ? 'text-red-600 font-medium' : 'text-gray-300'}`} onClick={() => startEdit(m)}>
                {months[m] ? fmtBRL(months[m]) : '-'}
              </button>
            )}
          </td>
        </React.Fragment>
      ))}
      {showTotals && <td className="px-2 py-2 text-center font-semibold text-red-700">{total ? fmtBRL(total) : '-'}</td>}
      <td className="px-2 py-2 text-center">
        <button onClick={() => onRemove(item._id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
      </td>
    </tr>
  );
};

const InstallmentCard: React.FC<{
  item: Installment;
  visibleMonths: string[];
  onAmountChange: (item: Installment, month: string, value: number) => void;
  onRemove: (id: string) => void;
  onDescChange: (item: Installment, desc: string) => void;
}> = ({ item, visibleMonths, onAmountChange, onRemove, onDescChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(item.description);
  const months = item.months || {};
  const total = visibleMonths.reduce((s, m) => s + (months[m] || 0), 0);
  const activeMonths = visibleMonths.filter(m => months[m]);

  const saveEdit = () => {
    if (editingMonth) {
      const num = parseFloat(editValue.replace(',', '.')) || 0;
      if (num !== (months[editingMonth] || 0)) onAmountChange(item, editingMonth, num);
      setEditingMonth(null);
    }
  };

  return (
    <div className="border-b last:border-b-0 bg-white">
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          {editingDesc ? (
            <input autoFocus value={descValue} onChange={e => setDescValue(e.target.value)}
              onBlur={() => { onDescChange(item, descValue); setEditingDesc(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { onDescChange(item, descValue); setEditingDesc(false); } if (e.key === 'Escape') { setEditingDesc(false); } }}
              className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          ) : (
            <button type="button" className="text-sm text-gray-800 font-medium truncate block w-full text-left" onClick={() => setEditingDesc(true)}>
              {item.description}
            </button>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-500">{activeMonths.length} meses</span>
            <span className="text-xs font-semibold text-red-700">{total ? fmtBRL(total) : 'R$ 0,00'}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 text-gray-500 hover:text-blue-600 rounded">
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button onClick={() => onRemove(item._id)} className="p-1.5 text-red-400 hover:text-red-600 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-2">
            {visibleMonths.map(m => (
              <div key={m} className="text-center">
                <span className="text-[10px] text-gray-500 block">{formatMonth(m)}</span>
                {editingMonth === m ? (
                  <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                    onBlur={saveEdit} onKeyDown={e => { if (e.key === 'Enter') { saveEdit(); } if (e.key === 'Escape') { setEditingMonth(null); } }}
                    className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-blue-500" />
                ) : (
                  <button type="button" onClick={() => { setEditingMonth(m); setEditValue(months[m] ? String(months[m]) : ''); }}
                    className={`text-xs w-full rounded py-0.5 ${months[m] ? 'text-red-600 font-medium bg-red-50' : 'text-gray-300 bg-gray-50'}`}>
                    {months[m] ? fmtBRL(months[m]) : '-'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallmentPage;
